import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { GAME_LIST } from "../../utils/gameList";
import { getGameReport } from "../../services/api";
import ProfitLossReportTable from "../profit-loss/components/ProfitLossReportTable";

/**
 * BettingPL Component
 *
 * P&L report by market for a specific user, matching the main P&L Report page layout.
 */
function BettingPL({ username }) {
  const location = useLocation()
  const locationUsername = location.state?.username

  const searchUsername = username && username !== 'Loading...' ? username : locationUsername

  const [eventFilter, setEventFilter] = useState("All")
  const [marketFilter, setMarketFilter] = useState("All")
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 7)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
  const [toDate, setToDate] = useState(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
  const [loading, setLoading] = useState(false)
  const [plData, setPlData] = useState([])
  const [selectedMarketBets, setSelectedMarketBets] = useState(null)
  const [selectedMarketName, setSelectedMarketName] = useState("")

  const processData = (data) => {
    const grouped = {}

    data.forEach(bet => {
      const date = bet.settled_at ? new Date(bet.settled_at).toLocaleDateString() : 'Unknown Date'
      const eventName = bet.match_title || bet.game_type || 'Unknown Event'
      const marketName = bet.market || 'Unknown Market'
      const marketId = `${eventName}-${marketName}`

      if (!grouped[date]) grouped[date] = {}
      if (!grouped[date][eventName]) grouped[date][eventName] = {}
      if (!grouped[date][eventName][marketId]) {
        grouped[date][eventName][marketId] = {
          id: marketId,
          marketName,
          pl: 0,
          bets: []
        }
      }

      grouped[date][eventName][marketId].pl += parseFloat(bet.netamount || 0)
      grouped[date][eventName][marketId].bets.push(bet)
    })

    const processedArray = Object.keys(grouped).map(date => ({
      date,
      events: Object.keys(grouped[date]).map(eventName => ({
        eventName,
        markets: Object.values(grouped[date][eventName])
      }))
    }))

    setPlData(processedArray)
  }

  const fetchPLData = async () => {
    if (!searchUsername) return
    setLoading(true)
    try {
      const params = {
        category: 'sport',
        event: eventFilter === 'All' ? undefined : eventFilter,
        fromDate,
        toDate,
        limit: 1000,
        offset: 0,
        search: searchUsername
      }

      const response = await getGameReport(params)

      if (response?.success && response.data) {
        processData(response.data)
      } else {
        setPlData([])
      }
    } catch (error) {
      console.error("Error fetching betting P&L:", error)
      setPlData([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPLData()
  }, [searchUsername])

  const handleSearch = () => {
    fetchPLData()
    setSelectedMarketBets(null)
  }

  const handleViewBets = (market) => {
    setSelectedMarketBets(market.bets)
    setSelectedMarketName(market.marketName)
  }

  const handleBackToReport = () => {
    setSelectedMarketBets(null)
    setSelectedMarketName("")
  }

  const calculateEventTotal = (markets) => {
    return markets.reduce((sum, market) => sum + market.pl, 0)
  }

  const calculateGrandTotal = () => {
    let total = 0
    plData.forEach((dateGroup) => {
      dateGroup.events.forEach((event) => {
        total += calculateEventTotal(event.markets)
      })
    })
    return total
  }

  return (
    <div className="w-full mx-auto bg-[#0f1224] h-auto">
      {/* Filter Section */}
      <div
        style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-10px" }}
        className="shadow-sm mt-2 px-1 py-2"
      >
        <div className="flex items-center gap-2 mb-2">
          <div style={{ width: "20%" }} className="flex flex-col gap-2">
            <label className="text-white text-[12px] font-bold">Event</label>
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
            >
              <option value="All">All</option>
              {GAME_LIST.map((game, index) => (
                <option key={`${game.value}-${index}`} value={game.value}>
                  {game.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ width: "20%" }} className="flex flex-col gap-2">
            <label className="text-white text-[12px] font-bold">Market</label>
            <select
              value={marketFilter}
              onChange={(e) => setMarketFilter(e.target.value)}
              className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
            >
              <option value="All">All</option>
            </select>
          </div>

          <div style={{ width: "16%" }} className="flex flex-col gap-2">
            <label className="text-white text-[12px] font-bold">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
            />
          </div>

          <div style={{ width: "16%" }} className="flex flex-col gap-2">
            <label className="text-white text-[12px] font-bold">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
            />
          </div>

          <div className="mt-4">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="bg-[#11374a] hover:bg-[#1a4f66] text-white text-[12px] font-bold px-4 py-2 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faSearch} className="h-3 w-3" />
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>
      </div>

      <div className="w-full h-px px-1 bg-white mb-2"></div>

      {/* Detail View (Bets Table) */}
      {selectedMarketBets ? (
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 py-2"
        >
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={handleBackToReport}
              className="text-[#2ce5dd] hover:text-white transition-colors flex items-center gap-1 text-sm font-bold"
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Report
            </button>
            <span className="text-white text-lg font-semibold ml-2">
              Bets for {selectedMarketName}
            </span>
          </div>
          <ProfitLossReportTable bets={selectedMarketBets} />
        </div>
      ) : (
        /* Report Table Section */
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 py-2"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Market
                  </th>
                  <th className="text-right px-2 py-1 text-white font-bold text-[14px]">
                    P&L
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="2" className="text-center py-4 text-gray-400">
                      Loading...
                    </td>
                  </tr>
                ) : plData.length === 0 ? (
                  <tr>
                    <td colSpan="2" className="text-center py-4 text-white">
                      No data found
                    </td>
                  </tr>
                ) : (
                  plData.map((dateGroup, dateIndex) => (
                    <React.Fragment key={dateIndex}>
                      {/* Date Header */}
                      <tr>
                        <td
                          colSpan="2"
                          className="px-2 py-2 text-white font-bold text-[14px]"
                          style={{ backgroundColor: "rgba(245, 245, 245, 0.4)" }}
                        >
                          {dateGroup.date}
                        </td>
                      </tr>

                      {dateGroup.events.map((event, eventIndex) => (
                        <React.Fragment key={eventIndex}>
                          {/* Event Header */}
                          <tr>
                            <td
                              colSpan="2"
                              className="px-2 py-2 text-white font-bold text-[13px] pl-6"
                              style={{ backgroundColor: "rgba(245, 245, 245, 0.3)" }}
                            >
                              {event.eventName}
                            </td>
                          </tr>

                          {/* Market Entries */}
                          {event.markets.map((market, marketIndex) => (
                            <tr
                              key={market.id}
                              className={`transition-colors ${
                                marketIndex % 2 === 0
                                  ? "bg-[rgba(245,245,245,0.05)]"
                                  : ""
                              }`}
                            >
                              <td className="py-1 px-2 pl-8 text-white text-[13px]" style={{ borderRight: "1px solid white" }}>
                                {market.marketName}{" "}
                                <button
                                  onClick={() => handleViewBets(market)}
                                  className="text-[#2ce5dd] hover:text-blue-300 hover:underline transition-colors text-[12px]"
                                >
                                  | View Bets
                                </button>
                              </td>
                              <td
                                className={`py-1 px-2 text-right font-bold text-[13px] ${
                                  market.pl >= 0 ? "text-green-500" : "text-red-500"
                                }`}
                              >
                                {market.pl >= 0 ? "+" : ""}
                                {market.pl.toFixed(2)}
                              </td>
                            </tr>
                          ))}

                          {/* Event Total */}
                          <tr>
                            <td
                              className="py-1 px-2 pl-6 text-white font-bold text-[13px]"
                              style={{ borderRight: "1px solid white", float: "right" }}
                            >
                              Total
                            </td>
                            <td
                              className={`py-1 px-2 text-right font-bold text-[13px] ${
                                calculateEventTotal(event.markets) >= 0
                                  ? "text-green-500"
                                  : "text-red-500"
                              }`}
                              style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                            >
                              {calculateEventTotal(event.markets) >= 0 ? "+" : ""}
                              {calculateEventTotal(event.markets).toFixed(2)}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))
                )}

                {/* Grand Total */}
                {plData.length > 0 && (
                  <tr>
                    <td
                      className="py-2 px-2 text-right text-white font-bold text-[14px]"
                      style={{ backgroundColor: "rgba(255, 255, 255, 0.15)" }}
                    >
                      Grand Total
                    </td>
                    <td
                      className={`py-2 px-2 text-right font-bold text-[14px] ${
                        calculateGrandTotal() >= 0 ? "text-green-500" : "text-red-500"
                      }`}
                      style={{ backgroundColor: "rgba(255, 255, 255, 0.15)" }}
                    >
                      {calculateGrandTotal() >= 0 ? "+" : ""}
                      {calculateGrandTotal().toFixed(2)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default BettingPL
